import React, { useCallback, useEffect, useMemo, useState } from "react";

// The Petty Cash list endpoints serve at most 200 rows a request - the service
// quietly turns any larger pageSize into 50 (PettyCashService, "pageSize is < 1
// or > 200 ? 50"). A report that asked for 500 in one go therefore only ever
// saw 50. This loads every page instead; MAX_PAGES only guards against a
// runaway range.
export const MAX_PAGE_SIZE = 200;
const MAX_PAGES = 50;

// Headless "fetch page N and report up" - RTK Query caches and invalidates each
// page like any other query, so a new voucher or adjustment still refreshes
// everything. Hooks can't be called in a loop, hence a component per page.
const PageLoader = ({ useQuery, params, cacheKey, onLoaded }) => {
  const { data } = useQuery(params);
  useEffect(() => {
    if (data) onLoaded(cacheKey, data.items || []);
  }, [data, cacheKey, onLoaded]);
  return null;
};

/**
 * Every row a paged Petty Cash list endpoint has for `baseParams`.
 * `idOf` must be a stable (module-level) function. Render the returned
 * `loaders` somewhere in the page - they draw nothing.
 *
 * Extracted 2026-09-15 from the Ledger page, which already worked this way,
 * so the Overview and Voucher Register load their rows the same way.
 */
export const useAllPages = (useQuery, baseParams, idOf, { skip = false } = {}) => {
  const params = useMemo(() => ({ ...baseParams, pageSize: MAX_PAGE_SIZE }), [baseParams]);
  const paramsKey = JSON.stringify(params);
  const { data, isLoading, isFetching } = useQuery({ ...params, page: 1 }, { skip });
  const totalCount = data?.totalCount ?? 0;
  const pageCount = Math.min(Math.ceil(totalCount / MAX_PAGE_SIZE), MAX_PAGES);

  const [extraPages, setExtraPages] = useState({});
  const onLoaded = useCallback(
    (key, items) => setExtraPages((prev) => (prev[key] === items ? prev : { ...prev, [key]: items })),
    []
  );

  const items = useMemo(() => {
    const byId = new Map();
    (data?.items || []).forEach((r) => byId.set(idOf(r), r));
    for (let p = 2; p <= pageCount; p += 1) {
      (extraPages[`${paramsKey}|${p}`] || []).forEach((r) => byId.set(idOf(r), r));
    }
    return [...byId.values()];
  }, [data, extraPages, pageCount, paramsKey, idOf]);

  const expectedCount = Math.min(totalCount, MAX_PAGES * MAX_PAGE_SIZE);
  const isComplete = !skip && !isLoading && items.length >= expectedCount;

  const loaders =
    skip || pageCount < 2
      ? null
      : Array.from({ length: pageCount - 1 }, (_, i) => i + 2).map((p) => (
          <PageLoader
            key={`${paramsKey}|${p}`}
            useQuery={useQuery}
            params={{ ...params, page: p }}
            cacheKey={`${paramsKey}|${p}`}
            onLoaded={onLoaded}
          />
        ));

  return {
    items,
    totalCount,
    isLoading,
    isFetching,
    isComplete,
    isCapped: totalCount > MAX_PAGES * MAX_PAGE_SIZE,
    cap: MAX_PAGES * MAX_PAGE_SIZE,
    loaders,
  };
};
